// student-profile-preview-modal.component.ts
import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService, LangChangeEvent } from '@ngx-translate/core';


interface PreviewDataItem {
  key: string;
  label: string;
  value: any;
}

@Component({
  selector: 'app-student-profile-preview-modal',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './student-profile-preview-modal.component.html',
  styleUrls: ['./student-profile-preview-modal.component.css']
})
export class StudentProfilePreviewModalComponent implements OnChanges {
  @Input() previewData: { [key: string]: any } = {};
  @Input() isVisible: boolean = false;

  @Output() confirm = new EventEmitter<void>(); 
  @Output() close = new EventEmitter<void>(); 

  displayData: PreviewDataItem[] = [];

  constructor( public translate: TranslateService) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['previewData'] && this.previewData) {
      this.formatAndSetDisplayData(this.previewData);
    }
  }

  onCancel(): void {
    this.close.emit();
  }

  onConfirmSubmit(): void {
    this.confirm.emit();
  }
  getTranslatedSeverity(severity: string | null | undefined): string {
    switch (severity) {
      case 'Mild':
        return this.translate.instant('ADD_STUDENT.PART_B.SEVERITY_MILD');
      case 'Moderate':
        return this.translate.instant('ADD_STUDENT.PART_B.SEVERITY_MODERATE');
      case 'Severe':
        return this.translate.instant('ADD_STUDENT.PART_B.SEVERITY_SEVERE');
      default:
        return 'N/A';
    }
  }

  private formatAndSetDisplayData(data: { [key: string]: any }): void {
    const displaySchema = [
      // 个人信息
      { key: 'chi_name', labelKey: 'ADD_STUDENT.PART_A.NAME_ZH_LABEL' },
      { key: 'eng_name', labelKey: 'ADD_STUDENT.PART_A.NAME_EN_LABEL' },
      { key: 'other_name', labelKey: 'ADD_STUDENT.PART_A.OTHER_NAME_LABEL' },
      { key: 'birth', labelKey: 'ADD_STUDENT.PART_A.DATE_OF_BIRTH_LABEL' },
      { key: 'nationality', labelKey: 'ADD_STUDENT.PART_A.NATIONALITY_LABEL' },
      { key: 'gender', labelKey: 'ADD_STUDENT.PART_A.GENDER_LABEL' },
      { key: 'age', labelKey: 'ADD_STUDENT.PART_A.AGE_LABEL' },
      { key: 'weight', labelKey: 'ADD_STUDENT.PART_A.WEIGHT_LABEL' },
      { key: 'height', labelKey: 'ADD_STUDENT.PART_A.HEIGHT_LABEL' },
      { key: 'head_size', labelKey: 'ADD_STUDENT.PART_A.HEAD_SIZE_LABEL' },
      { key: 'photo', labelKey: 'ADD_STUDENT.PART_A.PHOTO_LABEL' },
      // 监护人信息
      { key: 'parent_name', labelKey: 'ADD_STUDENT.PART_A.GUARDIAN_NAME_LABEL' },
      { key: 'parent_relationship', labelKey: 'ADD_STUDENT.PART_A.GUARDIAN_RELATIONSHIP_LABEL' },
      { key: 'parent_mobile', labelKey: 'ADD_STUDENT.PART_A.MOBILE_NO_LABEL' },
      { key: 'parent_email', labelKey: 'ADD_STUDENT.PART_A.GUARDIAN_EMAIL_LABEL' },
      { key: 'parent_home', labelKey: 'ADD_STUDENT.PART_A.HOME_NO_LABEL' },
      { key: 'parent_address', labelKey: 'ADD_STUDENT.PART_A.ADDRESS_LABEL' },
      { key: 'emergency_person', labelKey: 'ADD_STUDENT.PART_A.EMERGENCY_CONTACT_PERSON_LABEL' },
      { key: 'emergency_mobile', labelKey: 'ADD_STUDENT.PART_A.EMERGENCY_CONTACT_MOBILE_LABEL' },
      // 学校信息
      { key: 'school_name', labelKey: 'ADD_STUDENT.PART_B.SCHOOL_NAME_LABEL' },
      { key: 'school_address', labelKey: 'ADD_STUDENT.PART_B.SCHOOL_ADDRESS_LABEL' },
      { key: 'school_contact_person', labelKey: 'ADD_STUDENT.PART_B.CONTACT_PERSON_LABEL' },
      { key: 'school_tel', labelKey: 'ADD_STUDENT.PART_B.SCHOOL_CONTACT_NO_LABEL' },
      { key: 'school_fax', labelKey: 'ADD_STUDENT.PART_B.SCHOOL_FAX_NO_LABEL' },
      { key: 'school_email', labelKey: 'ADD_STUDENT.PART_B.SCHOOL_EMAIL_LABEL' },
      // 骑行评估
      { key: 'riding_experience', labelKey: 'ADD_STUDENT.PART_C.RIDING_EXPERIENCE_LABEL' },
      { key: 'assessor_name', labelKey: 'ADD_STUDENT.PART_C.ASSESSOR_NAME_LABEL' },
      { key: 'assessor_profession', labelKey: 'ADD_STUDENT.PART_C.ASSESSOR_PROFESSION_LABEL' },
      { key: 'assessor_tel', labelKey: 'ADD_STUDENT.PART_C.ASSESSOR_TEL_LABEL' },
      { key: 'assessment_date', labelKey: 'ADD_STUDENT.PART_C.ASSESSMENT_DATE_LABEL' },
      { key: 'assessor_email', labelKey: 'ADD_STUDENT.PART_C.ASSESSOR_EMAIL_LABEL' },
      { key: 'others', labelKey: 'ADD_STUDENT.PART_C.OTHERS_LABEL' },
      // 医疗信息
      { key: 'diagnosis', labelKey: 'ADD_STUDENT.PART_D.DIAGNOSIS_LABEL' },
      { key: 'other_therapy', labelKey: 'ADD_STUDENT.PART_D.OTHER_THERAPY_LABEL' },
      { key: 'medication', labelKey: 'ADD_STUDENT.PART_D.MEDICATION_LABEL' },
      // 医疗条件
      { key: 'asd', labelKey: 'ADD_STUDENT.PART_D.CONDITION_ASD_LABEL' },
      { key: 'asd_remark', labelKey: 'ADD_STUDENT.PART_D.CONDITION_ASD_REMARK_LABEL' },
      { key: 'adhd', labelKey: 'ADD_STUDENT.PART_D.CONDITION_ADHD_LABEL' },
      { key: 'adhd_remark', labelKey: 'ADD_STUDENT.PART_D.CONDITION_ADHD_REMARK_LABEL' },
      { key: 'sld', labelKey: 'ADD_STUDENT.PART_D.CONDITION_SLD_LABEL' },
      { key: 'sld_remark', labelKey: 'ADD_STUDENT.PART_D.CONDITION_SLD_REMARK_LABEL' },
      { key: 'ind', labelKey: 'ADD_STUDENT.PART_D.CONDITION_IND_LABEL' },
      { key: 'ind_remark', labelKey: 'ADD_STUDENT.PART_D.CONDITION_IND_REMARK_LABEL' },
      { key: 'comd', labelKey: 'ADD_STUDENT.PART_D.CONDITION_COMD_LABEL' },
      { key: 'comd_remark', labelKey: 'ADD_STUDENT.PART_D.CONDITION_COMD_REMARK_LABEL' },
      { key: 'visi', labelKey: 'ADD_STUDENT.PART_D.CONDITION_VISI_LABEL' },
      { key: 'visi_remark', labelKey: 'ADD_STUDENT.PART_D.CONDITION_VISI_REMARK_LABEL' },
      { key: 'heari', labelKey: 'ADD_STUDENT.PART_D.CONDITION_HEARI_LABEL' },
      { key: 'heari_remark', labelKey: 'ADD_STUDENT.PART_D.CONDITION_HEARI_REMARK_LABEL' },
      { key: 'bed', labelKey: 'ADD_STUDENT.PART_D.CONDITION_BED_LABEL' },
      { key: 'bed_remark', labelKey: 'ADD_STUDENT.PART_D.CONDITION_BED_REMARK_LABEL' },
      { key: 'phyd', labelKey: 'ADD_STUDENT.PART_D.CONDITION_PHYD_LABEL' },
      { key: 'phyd_remark', labelKey: 'ADD_STUDENT.PART_D.CONDITION_PHYD_REMARK_LABEL' },
      // 医疗相关文件
      { key: 'statement_rider_medical_condition', labelKey: 'ADD_STUDENT.PART_D.STATEMENT_MEDICAL_EPILEPSY_LABEL'},
      { key: 'medical_certificate_down_syndrome', labelKey: 'ADD_STUDENT.PART_D.MEDICAL_CERTIFICATE_DOWN_SYNDROME_LABEL'},
      { key: 'medical_certificate_heart_disease', labelKey: 'ADD_STUDENT.PART_D.MEDICAL_CERTIFICATE_HEAR_DISEASE_LABEL'},
      // 行走能力
      { key: 'ambulatoryIndependent', labelKey: 'ADD_STUDENT.PART_D.AMBULATORY_INDEPENDENT_LABEL' },
      { key: 'ambulatoryAssistanceNeeded', labelKey: 'ADD_STUDENT.PART_D.AMBULATORY_ASSISTANCE_NEEDED_LABEL' },
      { key: 'ambulatoryUnaided', labelKey: 'ADD_STUDENT.PART_D.AMBULATORY_UNAIDED_LABEL' },
      { key: 'ambulatoryCrutches', labelKey: 'ADD_STUDENT.PART_D.AMBULATORY_CRUTCHES_LABEL' },
      { key: 'ambulatoryWalkingStick', labelKey: 'ADD_STUDENT.PART_D.WALKING_STICK_LABEL' },
      { key: 'ambulatoryWalker', labelKey: 'ADD_STUDENT.PART_D.AMBULATORY_WALKER_LABEL' },
      { key: 'ambulatory_other', labelKey: 'ADD_STUDENT.PART_D.AMBULATORY_OTHER_LABEL' },
      { key: 'nonAmbulatoryChoice', labelKey: 'ADD_STUDENT.PART_D.NON_AMBULATORY_CHOICE_LABEL' },
      { key: 'part_body_affected', labelKey: 'ADD_STUDENT.PART_D.PART_BODY_AFFECTED_LABEL' },
      { key: 'prosthesis', labelKey: 'ADD_STUDENT.PART_D.PROSTHESIS_LABEL' },
      { key: 'head_control', labelKey: 'ADD_STUDENT.PART_D.HEAD_CONTROL_LABEL' },
      { key: 'trunk_control', labelKey: 'ADD_STUDENT.PART_D.TRUNK_CONTROL_LABEL' },
      // 同意声明
      { key: 'riderInfoConsent', labelKey: 'ADD_STUDENT.PART_E.RIDER_INFO_CONSENT_LABEL' },
      { key: 'rehabReleaseConsent', labelKey: 'ADD_STUDENT.PART_E.REHAB_RELEASE_CONSENT_LABEL' },
      { key: 'photoVideoConsent', labelKey: 'ADD_STUDENT.PART_E.PHOTO_VIDEO_CONSENT_LABEL' },
      { key: 'rdaLessonArrangementConsent', labelKey: 'ADD_STUDENT.PART_E.RDA_LESSON_ARRANGEMENT_CONSENT_LABEL' }
    ];

    const formattedData: PreviewDataItem[] = [];
    for (const item of displaySchema) {
      const value = data[item.key];
      if (value !== null && value !== undefined && (typeof value === 'boolean' || value !== '')) {
        formattedData.push({
          key: item.key,
          label: item.labelKey,
          value: value
        });
      }
    }
    this.displayData = formattedData;
  }
}